/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';

import TextField from '../../components/OpmsForm/Fields/TextField';

import Select from '../../components/OpmsForm/Fields/Select';

import ToggleButtonGroup from '../../components/OpmsForm/Fields/ToggleButtonGroup';

import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import DatePicker from '../../components/OpmsForm/Fields/DatePicker';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import TrialForm from '../../components/OpmsForm/TrialForm';
import { GetFieldLookupItem } from '../../context/LookupsContext';
import { FormContext, PatientContext } from '@ngt/online-patient-management-ui';
import LookupContext from '../../context/LookupContext';
import { Lookup } from '@ngt/online-patient-management-ui/dist/api/lookup';
import { useSnackbar } from 'notistack';
import { RequestState } from '@ngt/request-utilities';
import { Paper } from '@material-ui/core';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPreregistrationProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    button: {
        width: '100%',
        height: 32
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const AUSTRALIA = 'Australia';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const hasAustralianInstitution = ({ institution }: IOpmsContionParams<Dtos.Preregistration>) => !!institution && institution.country === AUSTRALIA;
const hasSignedMainConsentYes = ({ lookups, formState }: IOpmsContionParams<Dtos.Preregistration>) => GetFieldLookupItem(lookups, 'SignedMainConsentId', formState.values.signedMainConsentId!)?.type === Dtos.LookupYesNoType.Yes;
const hasSignedDatedOptionalConsentYes = ({ lookups, formState }: IOpmsContionParams<Dtos.Preregistration>) => GetFieldLookupItem(lookups, 'SignedDatedOptionalConsentId', formState.values.signedDatedOptionalConsentId!)?.type === Dtos.LookupYesNoType.Yes;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const ScreeningMessage: React.FunctionComponent<{ children?: React.ReactNode }> = ({ children }) => {
    const [patient] = React.useContext(PatientContext);

    const { enqueueSnackbar } = useSnackbar();

    const [, validation, , saveState, , ,] = React.useContext(FormContext);

    React.useEffect(() => {

        if (saveState.state === RequestState.Success) {
            let variant = 'success';
            let message = 'Screening has been successfully completed.'

            if (validation?.result === Dtos.ResultType.Valid) {
                enqueueSnackbar(message, { variant: variant as any, key: 'form-saved' });
            }
        }
    }, [saveState, patient, validation?.result, enqueueSnackbar])

    return <>
        { children }
    </>
}

const Preregistration: React.FunctionComponent<IPreregistrationProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="Screening Form"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.Preregistration}
                >
                    <ScreeningMessage>
                        <OpmsField
                            name="GenderId"
                            label={
                                patientCohortC ? 'Sex' : 'Gender'
                            }
                        >
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.Preregistration}
                            mode="Show"
                            condition={hasAustralianInstitution}
                            subscription={{}}
                        >
                            <OpmsField name="PatientInitials">
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                            <OpmsField name="PostcodeOfUsualResidence">
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                            <OpmsField name="IndigenousStatusId" xl={4} lg={4} md={6}>
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsField name="CountryOfBirth">
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                            <OpmsField name="MainLanguageForCommunication">
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <OpmsField name="SignedMainConsentId">
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.Preregistration}
                            mode="Enable"
                            condition={hasSignedMainConsentYes}
                            subscription={{ values: true }}
                        >
                            <OpmsField name="DateSignedMainConsent">
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField
                                name="SignedFutureContactConsentId"
                                instruction={
                                    !patientCohortC ? <small>
                                        <br />
                                        <i>Complete for patients signing the Amendment 2 Patient Information Sheet and Informed Consent. If joining <b>before</b> HREC (and RGO) approval of Amendment 2 and associated Information Sheet, select N/A.</i>
                                    </small>
                                        :
                                        undefined
                                }
                            >
                                <SignedFutureContactConsentInput />
                            </OpmsField>
                        </OpmsConditional>

                        {
                            !patientCohortC && <OpmsField name="SignedDatedOptionalConsentId">
                                <OpmsInput
                                    component={ToggleButtonGroup}
                                />
                            </OpmsField>
                        }

                        {
                            !patientCohortC && <OpmsConditional
                                formClass={Dtos.Preregistration}
                                mode="Enable"
                                condition={hasSignedDatedOptionalConsentYes}
                                subscription={{ values: true }}
                            >
                                <OpmsField name="DateSignedOptionalConsent">
                                    <OpmsInput
                                        component={DatePicker}
                                    />
                                </OpmsField>

                            </OpmsConditional>
                        }

                        <OpmsField name="SignedOptionalConsentId">
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                    </ScreeningMessage>
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

const SignedFutureContactConsentInput: React.FunctionComponent = () => {
    const lookup = React.useContext(LookupContext);

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    const lookupItemsToUse = lookup?.items?.filter(x => x.type !== Dtos.LookupYesNoNaType.NotApplicableOrNotAnnounced)

    return <>
        <OpmsInput
            component={ToggleButtonGroup}
            lookup={
                patientCohortC ? {
                    items: lookupItemsToUse
                } as Lookup : undefined
            }
        />
    </>
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default Preregistration;